import React, { useEffect, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { X } from 'lucide-react'
import Button from '../ui/Button'

interface SubmitForReviewDialogProps {
  open: boolean
  saving?: boolean
  onClose: () => void
  onSubmit: (description: string, revision: boolean) => void
}

export default function SubmitForReviewDialog({
  open,
  saving = false,
  onClose,
  onSubmit,
}: SubmitForReviewDialogProps) {
  const [description, setDescription] = useState('')
  const [revision, setRevision] = useState(true)

  useEffect(() => {
    if (!open) return
    setDescription('')
    setRevision(true)
  }, [open])

  const trimmedDescription = description.trim()

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 430, maxWidth: 'calc(100vw - 32px)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 1 }}>
        <span className="text-lg font-semibold text-gray-900">Submit Changes for Review</span>
        <IconButton onClick={onClose} disabled={saving} size="small" aria-label="Close">
          <X className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        <div className="space-y-3">
          <p className="text-sm leading-5 text-gray-500">
            A changeset detected. Please provide details for this submission.
          </p>
          <label className="block text-xs font-medium text-gray-700">
            Description <span className="text-red-600">*</span>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={saving}
              required
              autoFocus
              rows={3}
              className="mt-1 w-full resize-none rounded border border-gray-300 px-2 py-2 text-sm text-gray-900 outline-none focus:border-[#1D5176] focus:ring-1 focus:ring-[#1D5176] disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={revision}
              onChange={(event) => setRevision(event.target.checked)}
              disabled={saving}
              className="h-4 w-4 rounded border-gray-300 text-[#1D5176] focus:ring-[#1D5176]"
            />
            Revision
          </label>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(trimmedDescription, revision)} disabled={!trimmedDescription || saving}>
          {saving ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
